import Axios, { AxiosError, AxiosInstance } from "axios";

/**
 * Client for the new MoneeFlo backend (NestJS), which replaces Xano module by
 * module. Kept separate from the Xano axios instance on purpose: different
 * tokens, different headers, and the two will run side by side during cutover.
 */
export const MONEEFLO_API_URL =
  import.meta.env.VITE_MONEEFLO_API_URL ?? "http://localhost:3001";

const ACCESS_TOKEN_KEY = "mf.accessToken";
const REFRESH_TOKEN_KEY = "mf.refreshToken";

export const tokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  set(accessToken: string, refreshToken: string) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

export const moneefloApi: AxiosInstance = Axios.create({
  baseURL: `${MONEEFLO_API_URL}/v1`,
  headers: { "Content-Type": "application/json" },
});

moneefloApi.interceptors.request.use((config) => {
  const token = tokens.access;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Access tokens last 15 minutes, so a 401 usually just means "refresh me".
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(retry = true): Promise<string | null> {
  const attempted = tokens.refresh;
  if (!attempted) return null;
  try {
    const { data } = await Axios.post(`${MONEEFLO_API_URL}/v1/auth/refresh`, {
      refreshToken: attempted,
    });
    tokens.set(data.accessToken, data.refreshToken);
    return data.accessToken;
  } catch (error) {
    // Refresh tokens rotate, so a second tab that refreshed first invalidates
    // the one we just sent. Its replacement is already in localStorage: use it
    // rather than signing the person out of every tab.
    if (retry && tokens.refresh && tokens.refresh !== attempted) {
      return refreshAccessToken(false);
    }
    // A network blip is not a rejected session; only give up when the server
    // actually refused the token.
    const status = (error as AxiosError).response?.status;
    if (status === 401 || status === 403) tokens.clear();
    return null;
  }
}

moneefloApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (typeof error.config & {
          _retried?: boolean;
        })
      | undefined;
    // Only the endpoints that mint or revoke tokens must not be retried.
    // /auth/me is an ordinary call: a 401 there means "refresh me", and
    // treating it as unrefreshable logged people out every 15 minutes.
    const isTokenEndpoint =
      /\/auth\/(login|refresh|logout|forgot-password|reset-password)\b/.test(
        original?.url ?? "",
      );

    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !isTokenEndpoint
    ) {
      original._retried = true;
      refreshing ??= refreshAccessToken().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (token) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization =
          `Bearer ${token}`;
        return moneefloApi(original);
      }
    }
    return Promise.reject(error);
  },
);

/** The backend always answers errors as { message, errors?, requestId }. */
/** `addressLine1` -> "Address line 1", `gstNumber` -> "Gst number". */
function fieldLabel(key: string): string {
  const words = key
    .replace(/\./g, " ")
    .replace(/([a-z])([A-Z0-9])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function apiErrorMessage(error: unknown): string {
  const err = error as AxiosError<{
    message?: string;
    code?: string;
    errors?: Record<string, string>;
  }>;
  if (err?.code === "ERR_NETWORK") {
    return `Cannot reach the API at ${MONEEFLO_API_URL}. Is it running?`;
  }
  const data = err?.response?.data;
  // A validation failure names its fields; "Required" on its own tells the
  // desk nothing about which one.
  const fields = Object.entries(data?.errors ?? {}).filter(([k]) => k !== "_");
  if (data?.code === "validation_failed" && fields.length) {
    const missing = fields
      .filter(([, m]) => /^required$/i.test(m))
      .map(([k]) => fieldLabel(k));
    const invalid = fields
      .filter(([, m]) => !/^required$/i.test(m))
      .map(([k, m]) => `${fieldLabel(k)}: ${m}`);
    const parts: string[] = [];
    if (missing.length) parts.push(`Please fill in ${missing.join(", ")}`);
    parts.push(...invalid);
    return parts.join(". ");
  }
  return data?.message ?? "Something went wrong. Please try again.";
}

/**
 * Click-through preview only: nothing leaves the browser. Every call to the
 * API is answered locally; the business screens use their own mock, and
 * anything else gets an empty answer.
 */
if (import.meta.env.VITE_BUSINESS_PREVIEW === "1") {
  const zero = { grossMinor: "0", netMinor: "0", volumeMinor: "0", orders: 0, marginRate: "0", byCategory: [] };
  moneefloApi.defaults.adapter = async (config) => {
    const url = config.url ?? "";
    const data = url.includes("/dashboard/home")
      ? { today: zero, week: zero, month: zero }
      : Object.assign([], { items: [], data: [] }); // reads as a list or as { items }
    return { data, status: 200, statusText: "OK", headers: {}, config };
  };
}
